import React, {
  useMemo,
  useEffect,
  useCallback,
  useReducer,
  useContext,
} from 'react';
import cx from 'classnames';
import {
  useTable,
  useFlexLayout,
  useFilters,
  useSortBy,
  CellProps,
  Row,
} from 'react-table';

import { SavedViewContext } from './SavedViewContext';
import { NoResults } from './NoResults';
import { useBoardTableViewCardsQuery } from './BoardTableViewCardsQuery.generated';
import { useBoardTableViewMemberQuery } from './BoardTableViewMemberQuery.generated';
import { useBoardTableViewBoardsQuery } from './BoardTableViewBoardsQuery.generated';
import { useBoardTableViewOrganizationQuery } from './BoardTableViewOrganizationQuery.generated';
import { Spinner } from '@trello/nachos/spinner';
import { forTemplate } from '@trello/i18n';
import { InfiniteList, IterableType } from 'app/src/components/InfiniteList';
import { ViewsErrorMessage } from 'app/src/components/ViewsErrorMessage';
import { canEdit as canEditBoard } from '@trello/boards';

import styles from './BoardTableView.less';

import { NameCell } from './NameCell';
import { ListCell } from './ListCell';
import { MembersCell } from './MembersCell';
import { DueCell } from './DueCell';
import { LabelsCell } from './LabelsCell';

import { LabelModel } from 'app/gamma/src/types/models';

import { NameHeaderCell } from './NameHeaderCell';
import { ListHeaderCell } from './ListHeaderCell';
import { MembersHeaderCell } from './MembersHeaderCell';
import { LabelsHeaderCell } from './LabelsHeaderCell';
import { DueHeaderCell } from './DueHeaderCell';

import { DueFilter } from './filters/DueFilter';
import { sorters } from './sorters';
import { RowData } from './types';

import { NetworkStatus } from '@apollo/client';
import { ListFilter } from './filters/ListFilter';
import { MembersFilter } from './filters/MembersFilter';
import { LabelsFilter } from './filters/LabelsFilter';
import { useClearListFiltersWhenRemovingBoards } from './filters/ListFilterPopoverContent';
import { useClearMembersFiltersWhenRemovingBoards } from './filters/MembersFilterPopoverContent';
import { useClearLabelsFiltersWhenRemovingBoards } from './filters/LabelsFilterPopoverContent';

import { maxSelectableBoards } from 'app/src/components/TeamBoardSelector/maxSelectableBoards';
import { TableTestClasses, TableTestIds } from '@trello/test-ids';
import { EmptyBoardTableView } from './EmptyBoardTableView';
import { getIsDeactivated } from './getIsDeactivated';

const format = forTemplate('multi_board_table_view', {
  returnBlankForMissingStrings: true,
});

interface BoardTableViewProps {
  idOrg: string;
  idEnterprise?: string | null;
  boardIds: string[];
}

const REQUEST_PAGE_SIZE = 250;
const CLIENT_PAGE_SIZE = 25;
const DEFAULT_SORT_ORDER = 'boardOrder,listPos,pos';

interface RowComponentProps {
  row: Row<RowData>;
}

export const RowComponent: React.FunctionComponent<RowComponentProps> = React.memo(
  ({ row }) => (
    <div
      role="row"
      className={styles.tr}
      {...row.getRowProps()}
      data-card={row.original.id}
      data-test-class={TableTestClasses.TableRow}
    >
      {row.cells.map((cell) => {
        return (
          <span
            role="cell"
            className={cx(styles.td, styles[cell.column.id])}
            {...cell.getCellProps()}
          >
            {cell.render('Cell')}
          </span>
        );
      })}
    </div>
  ),
);

export const BoardTableView: React.FunctionComponent<BoardTableViewProps> = ({
  idOrg,
  idEnterprise,
  boardIds,
}: BoardTableViewProps) => {
  const isEmpty = boardIds.length === 0;
  const { savedView } = useContext(SavedViewContext);

  const {
    data: memberQueryData,
    error: memberQueryError,
    loading: memberQueryLoading,
  } = useBoardTableViewMemberQuery({
    skip: isEmpty,
  });

  const {
    data: boardsQueryData,
    error: boardsQueryError,
    loading: boardsQueryLoading,
    refetch: boardsQueryRefetch,
  } = useBoardTableViewBoardsQuery({
    variables: {
      idOrg,
      idBoards: boardIds.slice(0, maxSelectableBoards()),
    },
    skip: isEmpty,
  });

  // This should already be cached in the case that BoardTableView is wrapped by MultiBoardTableView
  const { data: organizationQueryData } = useBoardTableViewOrganizationQuery({
    variables: {
      idOrg,
    },
  });

  const [sortBy, setSortBy] = useReducer(
    (_state: string, sortValue: string | null) => {
      if (!sortValue) {
        return DEFAULT_SORT_ORDER;
      }
      return sortValue;
    },
    DEFAULT_SORT_ORDER,
  );
  const dueFilterParams = useMemo(() => {
    return {
      minDue: savedView.filters.due.getMinDue(),
      maxDue: savedView.filters.due.getMaxDue(),
      dueComplete: savedView.filters.due.getDueComplete(),
      overdue: savedView.filters.due.getOverdue(),
    };
  }, [savedView.filters.due]);

  const idLists = useMemo(() => Array.from(savedView.filters.list), [
    savedView.filters.list,
  ]);

  const idMembers = useMemo(() => Array.from(savedView.filters.members), [
    savedView.filters.members,
  ]);

  useEffect(() => {
    if (isEmpty && savedView.editable) {
      savedView.setFilter(new ListFilter());
      savedView.setFilter(new LabelsFilter());
      savedView.setFilter(new MembersFilter());
      savedView.setFilter(new DueFilter());
    }
  }, [isEmpty, savedView]);

  useClearListFiltersWhenRemovingBoards(boardsQueryData?.organization?.boards);
  useClearLabelsFiltersWhenRemovingBoards(
    boardsQueryData?.organization?.boards,
  );
  useClearMembersFiltersWhenRemovingBoards(
    boardsQueryData?.organization?.boards,
  );

  const {
    data: useBoardTableViewCardsQueryData,
    error: useBoardTableViewCardsQueryError,
    loading: useBoardTableViewCardsQueryLoading,
    fetchMore,
    networkStatus: useBoardTableViewCardsQueryNetworkStatus,
  } = useBoardTableViewCardsQuery({
    variables: {
      idBoards: boardIds.slice(0, maxSelectableBoards()),
      idOrg,
      pageSize: REQUEST_PAGE_SIZE,
      minDue: dueFilterParams.minDue,
      maxDue: dueFilterParams.maxDue,
      dueComplete: dueFilterParams.dueComplete,
      orOverdue: dueFilterParams.overdue,
      sortBy,
      idLists,
      labels: savedView.filters.labels.getLabelsForServer(),
      idMembers,
    },
    skip: isEmpty,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const isLoadingCards =
    useBoardTableViewCardsQueryNetworkStatus === NetworkStatus.loading;

  const isLoadingMore =
    useBoardTableViewCardsQueryNetworkStatus === NetworkStatus.fetchMore;

  const useBoardTableViewCardsQueryVariablesChanged =
    useBoardTableViewCardsQueryNetworkStatus === NetworkStatus.setVariables;

  const loading =
    memberQueryLoading ||
    boardsQueryLoading ||
    (isLoadingCards && !useBoardTableViewCardsQueryVariablesChanged);
  const error =
    memberQueryError || boardsQueryError || useBoardTableViewCardsQueryError;

  const currentCursor =
    useBoardTableViewCardsQueryData?.organization?.cards?.cursor;

  const totalCards =
    useBoardTableViewCardsQueryData?.organization?.cards?.total ?? 0;

  const loadMoreCards = useCallback(() => {
    if (!isLoadingMore && currentCursor) {
      fetchMore({
        variables: {
          cursor: currentCursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          const newQueryResults = fetchMoreResult;

          if (!newQueryResults) {
            return prev;
          }

          const prevCards = prev?.organization?.cards.cards || [];
          const newCards = newQueryResults.organization?.cards.cards || [];

          if (newQueryResults.organization) {
            newQueryResults.organization.cards.cards = [
              ...prevCards,
              ...newCards,
            ];
          }

          return newQueryResults;
        },
      });
    }
  }, [currentCursor, fetchMore, isLoadingMore]);

  const boardById = useMemo(() => {
    const boards = boardsQueryData?.organization?.boards ?? [];
    const currentMember = memberQueryData?.member;
    const organization = organizationQueryData?.organization;
    const enterprise = organizationQueryData?.organization?.enterprise;

    return new Map(
      boards.map((board) => {
        const isDeactivated = getIsDeactivated(board);

        const memberById = new Map(
          board.members.map((member) => [
            member.id,
            {
              ...member,
              deactivated: isDeactivated(member.id),
            },
          ]),
        );

        return [
          board.id,
          {
            ...board,
            memberById,
            editable: currentMember
              ? canEditBoard(
                  currentMember,
                  board,
                  organization || null,
                  enterprise || null,
                )
              : false,
          },
        ];
      }),
    );
  }, [boardsQueryData, memberQueryData, organizationQueryData]);

  const listById = useMemo(() => {
    const boardsData = boardsQueryData?.organization?.boards ?? [];
    const lists = boardsData.flatMap((board) => board.lists);
    return new Map(lists.map((list) => [list.id, list]));
  }, [boardsQueryData]);

  const cards = useMemo(() => {
    const orgCards =
      useBoardTableViewCardsQueryData?.organization?.cards?.cards || [];

    return orgCards.map((card) => {
      const currentBoard = boardById.get(card.idBoard);
      const currentList = listById.get(card.idList);

      return {
        ...card,
        board: currentBoard,
        list: currentList,
        boardName: currentBoard?.name ?? '',
        boardBackgroundColor: currentBoard?.prefs?.backgroundColor,
        boardBackgroundImage: currentBoard?.prefs?.backgroundImage,
        boardBackgroundImageScaled: currentBoard?.prefs?.backgroundImageScaled,
        boardBackgroundTile: currentBoard?.prefs?.backgroundTile,
        listName: currentList?.name ?? '',
        editable: currentBoard?.editable ?? false,
        members: card.idMembers
          .map((idMember) => currentBoard?.memberById.get(idMember))
          .filter((member) => !!member),
      };
    });
  }, [boardById, listById, useBoardTableViewCardsQueryData]);

  const colorBlind = memberQueryData?.member?.prefs?.colorBlind || false;

  const sortTypes = useMemo(() => sorters, []);

  const columns = useMemo(
    () => [
      {
        id: 'card',
        Header: NameHeaderCell,
        accessor: 'name',
        Cell: (props: CellProps<RowData>) => (
          <NameCell
            id={props.row.original.id}
            name={props.row.original.name}
            url={props.row.original.url}
            idOrg={idOrg}
            idBoard={props.row.original.idBoard}
            boardName={props.row.original.boardName}
            boardBackgroundColor={props.row.original.boardBackgroundColor}
            boardBackgroundImageScaled={
              props.row.original.boardBackgroundImageScaled
            }
            boardBackgroundImage={props.row.original.boardBackgroundImage}
            boardBackgroundTile={props.row.original.boardBackgroundTile}
            editable={props.row.original.editable}
          />
        ),
      },
      {
        id: 'list',
        Header: (props: CellProps<RowData>) => (
          <ListHeaderCell idBoards={boardIds} idOrg={idOrg} />
        ),
        accessor: 'list.name',
        Cell: (props: CellProps<RowData>) => (
          <ListCell
            listName={props.row.original.listName}
            idCard={props.row.original.id}
            idList={props.row.original.idList}
            idBoard={props.row.original.idBoard}
            idOrg={idOrg}
            editable={props.row.original.editable}
          />
        ),
      },
      {
        id: 'labels',
        Header: (props: CellProps<RowData>) => (
          <LabelsHeaderCell idBoards={boardIds} idOrg={idOrg} />
        ),
        accessor: 'labels',
        Cell: (props: CellProps<RowData>) => (
          <LabelsCell
            idCard={props.row.original.id}
            idBoard={props.row.original.idBoard}
            idOrg={idOrg}
            colorBlind={colorBlind}
            labels={props.row.original.labels as LabelModel[]}
            refetchBoardTableData={boardsQueryRefetch}
            editable={props.row.original.editable}
          />
        ),
      },
      {
        id: 'members',
        Header: (props: CellProps<RowData>) => (
          <MembersHeaderCell idBoards={boardIds} idOrg={idOrg} />
        ),
        accessor: 'idMembers',
        Cell: (props: CellProps<RowData>) => (
          <MembersCell
            idCard={props.row.original.id}
            idBoard={props.row.original.idBoard}
            idOrg={idOrg}
            memberIds={props.row.original.idMembers}
            members={props.row.original.members}
            editable={props.row.original.editable}
          />
        ),
      },
      {
        id: 'due',
        Header: (props: CellProps<RowData>) => (
          // TODO: props.column.*sort* is denormalized from `sortBy` local state in this component
          <DueHeaderCell
            toggleSortBy={props.column.toggleSortBy}
            clearSortBy={props.column.clearSortBy}
            isSorted={props.column.isSorted}
            isSortedDesc={props.column.isSortedDesc}
            idOrg={idOrg}
            setServerSortBy={setSortBy}
          />
        ),
        accessor: 'due',
        Cell: (props: CellProps<RowData>) => (
          <DueCell
            due={props.row.original.due}
            dueComplete={props.row.original.dueComplete}
            dueReminder={props.row.original.dueReminder}
            idCard={props.row.original.id}
            editable={props.row.original.editable}
            idOrg={idOrg}
          />
        ),
        sortType: 'due',
      },
    ],
    // Be careful about dependencies to this useMemo. It will cause the table
    // cells to re-render with a "different component," so they'll lose local
    // state, causing bugs like closing visible popovers.
    [boardIds, boardsQueryRefetch, colorBlind, idOrg],
  );

  const {
    getTableProps,
    getTableBodyProps,
    rows,
    prepareRow,
    headerGroups,
  } = useTable(
    {
      columns,
      data: cards,
      sortTypes,
      manualFilters: true,
      manualSortBy: true,
    },
    useFilters,
    useSortBy,
    useFlexLayout,
  );

  const ItemRenderer = useCallback(
    (index: number) => {
      const row = rows[index] as Row<RowData>;
      prepareRow(row);

      return <RowComponent key={row.id} row={row} />;
    },
    [prepareRow, rows],
  );

  const ItemsRenderer = useCallback(
    (items: IterableType, ref: (instance: HTMLDivElement) => void) => (
      <div className={styles.tbody} {...getTableBodyProps()} ref={ref}>
        {items}
      </div>
    ),
    [getTableBodyProps],
  );

  const handleCopy = useCallback(
    (e: React.ClipboardEvent<HTMLDivElement>) => {
      const selection = document.getSelection();
      if (!selection) {
        return;
      }

      const idCards = new Set<string>();
      let shouldCopyHeaders = false;

      for (let i = 0; i < selection.rangeCount; i++) {
        const clonedContents = selection.getRangeAt(i).cloneContents();

        if (
          !shouldCopyHeaders &&
          clonedContents.querySelector('[data-header]')
        ) {
          shouldCopyHeaders = true;
        }

        const cardRows = clonedContents.querySelectorAll('[data-card]');

        for (const cardRow of cardRows) {
          const dataRow = (cardRow as HTMLElement).dataset.card;
          if (dataRow) {
            idCards.add(dataRow);
          }
        }
      }

      if (idCards.size) {
        const rowsForClipboard = [];

        if (shouldCopyHeaders) {
          const columnKeys = columns.map((column) => column.id);
          rowsForClipboard.push(
            columnKeys.map((columnKey) => {
              if (columnKey === 'due') {
                return format('due-date');
              }

              return format(columnKey) || columnKey;
            }),
          );
        }

        const cardsToCopy = cards.filter((card) => idCards.has(card.id));

        for (const card of cardsToCopy) {
          rowsForClipboard.push([
            card.name,
            card.listName,
            card.labels.map((l) => l.name || l.color).join(','),
            card.members
              .map((m) => m?.fullName)
              .filter((m) => !!m)
              .join(','),
            card.due ?? '',
          ]);
        }

        e.preventDefault();

        e.clipboardData.setData(
          'text/plain',
          rowsForClipboard.map((row) => row.join('\t')).join('\r\n') + '\r\n',
        );
      }
    },
    [cards, columns],
  );

  if (isEmpty) {
    return <EmptyBoardTableView />;
  }

  if (error) {
    let secondaryMessage;
    const message = error.networkError?.message;
    if (message === 'Error: UNAUTHORIZED')
      secondaryMessage = format('error-unauthorized');
    else if (message === 'Error: BOARDS_REQUIRED')
      secondaryMessage = format('error-boards-required');
    else if (message === 'Error: BOARD_ARG_LIMIT_EXCEEDED')
      secondaryMessage = format('error-board-arg-limit-exceeded');
    else if (message === 'Error: CARD_QUERY_LIMIT_EXCEEDED')
      secondaryMessage = format('error-card-query-limit-reached');
    else secondaryMessage = format('error-default');

    return (
      <ViewsErrorMessage
        screenEventName="multiBoardTableViewDisabledScreen"
        analyticsContainers={{ organization: { id: idOrg } }}
        headerMessage={format('oops-something-went-wrong')}
        secondaryMessage={secondaryMessage}
      ></ViewsErrorMessage>
    );
  }

  if (loading) {
    return <Spinner centered />;
  }

  // Table roles from https://www.w3.org/TR/wai-aria-practices/examples/table/table.html
  return (
    <>
      <div
        className={styles.table}
        data-test-id={TableTestIds.TableBody}
        {...getTableProps()}
        role="table"
        onCopy={handleCopy}
      >
        <div className={styles.thead}>
          {headerGroups.map((headerGroup) => (
            <div
              className={styles.tr}
              {...headerGroup.getHeaderGroupProps()}
              data-header="header"
            >
              {headerGroup.headers.map((column) => (
                <span
                  role="columnheader"
                  className={cx(styles.th, styles[column.id])}
                  {...column.getHeaderProps()}
                >
                  {column.render('Header')}
                </span>
              ))}
            </div>
          ))}
        </div>
        {cards.length > 0 ? (
          <InfiniteList
            itemCount={cards.length}
            pageSize={CLIENT_PAGE_SIZE}
            awaitMore={cards.length < totalCards}
            loadMore={loadMoreCards}
            renderItem={ItemRenderer}
            itemsRenderer={ItemsRenderer}
            isLoading={isLoadingMore}
          />
        ) : useBoardTableViewCardsQueryLoading ? (
          <Spinner centered />
        ) : (
          <NoResults idOrganization={idOrg} />
        )}
      </div>
    </>
  );
};
